import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { getRolesByTenantId, getSupportPreview, getUserInfoBySubId } from '../api/tenantApi';
import { getCurrentUserId } from '../authUtils';
import getTenantIdFromURL from '../utils/getTenantIdFromURL';
import {
  buildPermissionsFromRole,
  findRoleById,
  fullAccessPermissions,
  getUserRoleId,
} from '../utils/rolePermissions';
import {
  buildReadOnlyPreviewPermissions,
  getSupportPreviewUserId,
} from '../utils/supportPreview';

const RolePermissionsContext = createContext({
  loading: true,
  role: null,
  roleId: null,
  permissions: fullAccessPermissions(),
  isRestricted: false,
  isSupportPreview: false,
  supportPreview: null,
  refresh: () => {},
});

export function RolePermissionsProvider({ children }) {
  const isAdmin = useSelector((state) => state.userInfo.isAdmin);
  const tenantId = useSelector((state) => state.userInfo.tenantId);
  const [loading, setLoading] = useState(true);
  const [role, setRole] = useState(null);
  const [roleId, setRoleId] = useState(null);
  const [supportPreview, setSupportPreview] = useState(null);
  const previewUserId = typeof window !== 'undefined'
    ? getSupportPreviewUserId(window.location.search)
    : null;
  const previewTenantId = typeof window !== 'undefined' && previewUserId
    ? getTenantIdFromURL()
    : null;
  const isSupportPreview = Boolean(isAdmin && previewUserId && previewTenantId);

  const loadRolePermissions = useCallback(async () => {
    if (isAdmin && previewUserId && previewTenantId) {
      setLoading(true);
      try {
        const response = await getSupportPreview(previewTenantId, previewUserId);
        const preview = response?.data || response;
        const previewRoleId = getUserRoleId(preview);
        setSupportPreview(preview);
        setRoleId(previewRoleId);

        if (!previewRoleId) {
          setRole(null);
          return;
        }

        const rolesResponse = await getRolesByTenantId(previewTenantId);
        setRole(findRoleById(rolesResponse?.data || [], previewRoleId));
      } catch (error) {
        console.error('Failed to load support preview:', error);
        setSupportPreview(null);
        setRole(null);
        setRoleId(null);
      } finally {
        setLoading(false);
      }
      return;
    }

    setSupportPreview(null);
    const subId = getCurrentUserId();
    if (!subId) {
      setRole(null);
      setRoleId(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const user = await getUserInfoBySubId(subId);
      const userRoleId = getUserRoleId(user);
      setRoleId(userRoleId);

      if (!userRoleId || !tenantId) {
        setRole(null);
        return;
      }

      const rolesResponse = await getRolesByTenantId(tenantId);
      const roles = rolesResponse?.data || [];
      const matchedRole = findRoleById(roles, userRoleId);
      setRole(matchedRole || null);
    } catch (error) {
      console.error('Failed to load role permissions:', error);
      setRole(null);
    } finally {
      setLoading(false);
    }
  }, [isAdmin, previewTenantId, previewUserId, tenantId]);

  useEffect(() => {
    loadRolePermissions();
  }, [loadRolePermissions]);

  const permissions = useMemo(() => {
    // Fail closed from the first render (and if lookup fails): a URL requesting
    // preview mode must never momentarily expose the platform admin's write access.
    if (isSupportPreview) {
      return buildReadOnlyPreviewPermissions(role);
    }
    if (isAdmin) {
      return fullAccessPermissions();
    }
    return buildPermissionsFromRole(role);
  }, [isAdmin, isSupportPreview, role]);

  const isRestricted = isSupportPreview || (!isAdmin && permissions.isViewOnly);

  const value = useMemo(
    () => ({
      loading,
      role,
      roleId,
      permissions,
      isRestricted,
      isSupportPreview,
      supportPreview,
      refresh: loadRolePermissions,
    }),
    [
      loading,
      role,
      roleId,
      permissions,
      isRestricted,
      isSupportPreview,
      supportPreview,
      loadRolePermissions,
    ]
  );

  return (
    <RolePermissionsContext.Provider value={value}>
      {children}
    </RolePermissionsContext.Provider>
  );
}

export function useRolePermissions() {
  return useContext(RolePermissionsContext);
}
